import React, { useState } from 'react';
import { Plus, Users, ClipboardList, Star, Search, CheckCircle, MapPin, User, Briefcase } from 'lucide-react';
interface TeamMember {
  id: number;
  name: string;
  initials: string;
  role: string;
  status: 'available' | 'on-duty' | 'unavailable';
  jobsCompleted: number;
  rating: number;
  currentJob?: string;
  avatarColor: string;
}
interface TeamPageProps {
  onViewProfile?: (memberId: number) => void;
}
export function TeamPage({
  onViewProfile
}: TeamPageProps) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const members: TeamMember[] = [{
    id: 1,
    name: 'Lucia Martini',
    initials: 'LM',
    role: 'Operatore sul Campo',
    status: 'on-duty',
    jobsCompleted: 47,
    rating: 4.8,
    currentJob: 'Via Roma 4',
    avatarColor: 'bg-teal-deep/10 text-teal-deep'
  }, {
    id: 2,
    name: 'Marco Rossi',
    initials: 'MR',
    role: 'Operatore Senior',
    status: 'available',
    jobsCompleted: 124,
    rating: 4.9,
    avatarColor: 'bg-teal-deep/10 text-teal-deep'
  }, {
    id: 3,
    name: 'Giulia Bianchi',
    initials: 'GB',
    role: 'Operatore sul Campo',
    status: 'unavailable',
    jobsCompleted: 32,
    rating: 4.5,
    avatarColor: 'bg-teal-deep/10 text-teal-deep'
  }, {
    id: 4,
    name: 'Alessandro Verdi',
    initials: 'AV',
    role: 'Operatore sul Campo',
    status: 'available',
    jobsCompleted: 15,
    rating: 4.2,
    avatarColor: 'bg-teal-deep/10 text-teal-deep'
  }, {
    id: 5,
    name: 'Francesca Neri',
    initials: 'FN',
    role: 'Specialista Vetri',
    status: 'on-duty',
    jobsCompleted: 89,
    rating: 4.9,
    currentJob: 'Uffici Centro Direzionale',
    avatarColor: 'bg-teal-deep/10 text-teal-deep'
  }, {
    id: 6,
    name: 'Roberto Gallo',
    initials: 'RG',
    role: 'Operatore sul Campo',
    status: 'available',
    jobsCompleted: 56,
    rating: 4.7,
    avatarColor: 'bg-teal-deep/10 text-teal-deep'
  }];
  const filteredMembers = members.filter(member => {
    const matchesFilter = activeFilter === 'all' || activeFilter === 'available' && member.status === 'available' || activeFilter === 'on-duty' && member.status === 'on-duty' || activeFilter === 'unavailable' && member.status === 'unavailable';
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <span className="bg-green-100 text-green-800 rounded-full text-xs font-medium px-2 py-0.5" data-id="element-2446">
            Disponibile
          </span>;
      case 'on-duty':
        return <span className="bg-blue-100 text-blue-700 rounded-full text-xs font-medium px-2 py-0.5" data-id="element-2447">
            In Servizio
          </span>;
      case 'unavailable':
        return <span className="bg-gray-100 text-gray-600 rounded-full text-xs font-medium px-2 py-0.5" data-id="element-2448">
            Non Disponibile
          </span>;
      default:
        return null;
    }
  };
  return <div className="space-y-8" data-id="element-2449">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2450">
        <div data-id="element-2451">
          <h1 className="text-xl lg:text-2xl font-bold font-montserrat text-navy mb-1" data-id="element-2452">
            Il Mio Team
          </h1>
          <p className="text-sm text-gray-500" data-id="element-2453">Gestisci i tuoi collaboratori</p>
        </div>
        <button className="bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-sm min-h-[36px] px-5 py-2 flex items-center gap-2 shadow-sm transition-colors" data-id="element-2454">
          <Plus size={16} data-id="element-2455" />
          Aggiungi Collaboratore
        </button>
      </div>

      {/* Team Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2456">
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-id="element-2457">
          <div className="flex justify-between items-start mb-2" data-id="element-2458">
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-2459">
              Collaboratori Attivi
            </span>
            <div className="p-1.5 bg-gray-50 rounded-lg" data-id="element-2460">
              <Users className="w-5 h-5 text-navy" data-id="element-2461" />
            </div>
          </div>
          <span className="text-2xl font-bold text-navy" data-id="element-2462">6</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-id="element-2463">
          <div className="flex justify-between items-start mb-2" data-id="element-2464">
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-2465">
              In Servizio Oggi
            </span>
            <div className="p-1.5 bg-blue-50 rounded-lg" data-id="element-2466">
              <ClipboardList className="w-5 h-5 text-blue-600" data-id="element-2467" />
            </div>
          </div>
          <span className="text-2xl font-bold text-navy" data-id="element-2468">4</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-id="element-2469">
          <div className="flex justify-between items-start mb-2" data-id="element-2470">
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-2471">
              Performance Media
            </span>
            <div className="p-1.5 bg-amber-50 rounded-lg" data-id="element-2472">
              <Star className="w-5 h-5 text-amber-500" data-id="element-2473" />
            </div>
          </div>
          <span className="text-2xl font-bold text-navy" data-id="element-2474">
            4.7<span className="text-sm text-gray-400 font-normal" data-id="element-2475">/5</span>
          </span>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center" data-id="element-2476">
        <div className="relative w-full lg:w-80" data-id="element-2477">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-2478" />
          <input type="text" placeholder="Cerca per nome..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-2479" />
        </div>

        <div className="bg-gray-100 p-1 rounded-lg flex overflow-x-auto max-w-full" data-id="element-2480">
          {[{
          id: 'all',
          label: 'Tutti'
        }, {
          id: 'available',
          label: 'Disponibili'
        }, {
          id: 'on-duty',
          label: 'In Servizio'
        }, {
          id: 'unavailable',
          label: 'Non Disponibili'
        }].map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${activeFilter === filter.id ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-2481">
              {filter.label}
            </button>)}
        </div>
      </div>

      {/* Team Members Grid */}
      {filteredMembers.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-2482">
          {filteredMembers.map(member => <div key={member.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col h-full" data-id="element-2483">
              <div className="flex justify-between items-start mb-4" data-id="element-2484">
                <div className="flex gap-3" data-id="element-2485">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${member.avatarColor}`} data-id="element-2486">
                    {member.initials}
                  </div>
                  <div data-id="element-2487">
                    <h3 className="font-semibold text-navy text-sm" data-id="element-2488">
                      {member.name}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-2489">{member.role}</p>
                  </div>
                </div>
                {getStatusBadge(member.status)}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500 mb-3" data-id="element-2490">
                <div className="flex items-center gap-1.5" data-id="element-2491">
                  <CheckCircle size={14} className="text-gray-400" data-id="element-2492" />
                  <span className="text-xs" data-id="element-2493">
                    {member.jobsCompleted} lavori completati
                  </span>
                </div>
              </div>

              {member.currentJob && <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 mb-4 flex items-center gap-2" data-id="element-2494">
                  <MapPin size={14} className="text-blue-500 flex-shrink-0" data-id="element-2495" />
                  <p className="text-xs font-medium text-navy truncate" data-id="element-2496">
                    {member.currentJob}
                  </p>
                </div>}

              <div className="mt-auto pt-2 grid grid-cols-2 gap-3" data-id="element-2497">
                <button onClick={() => onViewProfile && onViewProfile(member.id)} className="w-full py-2 px-3 border border-gray-200 rounded-lg text-xs font-medium text-navy hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" data-id="element-2498">
                  <User size={14} data-id="element-2499" />
                  Profilo
                </button>
                <button className="w-full py-2 px-3 bg-teal-deep text-white rounded-lg text-xs font-medium hover:bg-teal-deep/90 transition-colors flex items-center justify-center gap-2" data-id="element-2500">
                  <Briefcase size={14} data-id="element-2501" />
                  Assegna
                </button>
              </div>
            </div>)}
        </div> : <div className="text-center py-16 bg-white border border-gray-200 rounded-xl border-dashed" data-id="element-2502">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2503">
            <Users className="w-8 h-8 text-gray-400" data-id="element-2504" />
          </div>
          <h3 className="text-lg font-semibold text-navy mb-1" data-id="element-2505">
            Nessun collaboratore trovato
          </h3>
          <p className="text-gray-500 text-sm mb-6" data-id="element-2506">
            Prova a modificare i filtri o la ricerca.
          </p>
          <button onClick={() => {
        setActiveFilter('all');
        setSearchQuery('');
      }} className="text-teal-deep font-medium text-sm hover:underline" data-id="element-2507">
            Cancella filtri
          </button>
        </div>}
    </div>;
}